import { RoundedButton } from '@/components/rounded-button';
import { TooltipIcon } from '@/components/tooltip-icon';
import { wrappedPlumeSymbol } from '@/constants/shared';
import { useRoycoPositionDataForVaultKey } from '@/hooks/useRoycoPositionsData';
import { getIconPathFromTokenSymbol, getTimeUntilUnlock } from '@/utils';
import { Box, Card, Flex, FormatNumber, Heading, HStack, Image, Table, Text } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router';
import { PLUME_INCENTIVES_WITHDRAW_TEXT } from './constants';
import { useCurrentVault } from './DepositsAndWithdrawals/hooks/useCurrentVault';

export const RoycoPositions = () => {
  const { vault, vaultKey } = useCurrentVault();
  const { currentVaultRoycoPositionData } = useRoycoPositionDataForVaultKey({ vaultKey });
  const navigate = useNavigate();
  const { pathname } = useLocation();
  if (!vault) return;
  if (!currentVaultRoycoPositionData?.length) return null;
  return (
    <Box>
      <Heading size="2xl" fontWeight="700" mb={4}>
        Royco Positions
      </Heading>
      <Card.Root borderRadius="2xl">
        <Card.Body>
          <Box maxH={300} overflow="auto">
            <Table.Root>
              <Table.Header position="sticky" top={0} bgColor="bg" opacity={1} zIndex="sticky">
                <Table.Row>
                  <Table.ColumnHeader fontSize="sm" color="fg.muted">
                    Market
                  </Table.ColumnHeader>
                  <Table.ColumnHeader fontSize="sm" color="fg.muted">
                    Deposit
                  </Table.ColumnHeader>
                  <Table.ColumnHeader fontSize="sm" color="fg.muted">
                    Incentives
                  </Table.ColumnHeader>
                  <Table.ColumnHeader fontSize="sm" color="fg.muted">
                    Time to Incentive
                    <TooltipIcon ml={1} content={PLUME_INCENTIVES_WITHDRAW_TEXT} />
                  </Table.ColumnHeader>
                  <Table.ColumnHeader fontSize="sm"></Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {currentVaultRoycoPositionData.map((position) => {
                  const { claimedWPlumeIncentiveToken, unclaimedWPlumeIncentiveToken, lockedInputToken } = position;
                  const totalWPlumeEarned =
                    claimedWPlumeIncentiveToken?.tokenAmount ?? unclaimedWPlumeIncentiveToken?.tokenAmount ?? 0;
                  const totalWPlumeEarnedUsd =
                    claimedWPlumeIncentiveToken?.tokenAmountUsd ?? unclaimedWPlumeIncentiveToken?.tokenAmountUsd ?? 0;
                  return (
                    <Table.Row key={position.id} fontSize="sm" fontWeight="500" color="fg">
                      <Table.Cell>
                        <HStack gap={2}>
                          <Image src={getIconPathFromTokenSymbol(vault?.token.symbol ?? '')} boxSize={10} />
                          <Box spaceY={1}>
                            <Text>{vaultKey} Royco Market</Text>
                            <Text color="fg.muted">{vaultKey}</Text>
                          </Box>
                        </HStack>
                      </Table.Cell>
                      <Table.Cell>
                        <Box spaceY={1}>
                          <Text>
                            {lockedInputToken.tokenAmount} {vaultKey}
                          </Text>
                          <Text color="fg.muted">
                            <FormatNumber value={lockedInputToken.tokenAmountUsd} style="currency" currency="USD" />
                          </Text>
                        </Box>
                      </Table.Cell>
                      <Table.Cell>
                        <Box spaceY={1}>
                          <Text>
                            <FormatNumber value={totalWPlumeEarned} maximumFractionDigits={2} /> {wrappedPlumeSymbol}
                          </Text>
                          <Text color="fg.muted">
                            <FormatNumber value={totalWPlumeEarnedUsd} style="currency" currency="USD" />
                          </Text>
                        </Box>
                      </Table.Cell>
                      <Table.Cell>{getTimeUntilUnlock(Number(lockedInputToken.unlockTimestamp))}</Table.Cell>
                      <Table.Cell>
                        <Flex w="full" justifyContent="end">
                          <RoundedButton
                            onClick={() =>
                              navigate(
                                `${pathname}/withdraw/steps?royco_position_id=${position.id}&from_amount=${lockedInputToken.tokenAmount}`,
                              )
                            }
                          >
                            {lockedInputToken.isUnlocked ? 'Withdraw & Claim' : 'Withdraw & Forfeit'}
                          </RoundedButton>
                        </Flex>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table.Root>
          </Box>
        </Card.Body>
      </Card.Root>
    </Box>
  );
};
